import {
    makeSignalContainer,
    makeEffectRecord,
    runEffectV2,
    stopEffectV2,
    trackV2,
    triggerV2,
    type SignalContainer,
    type EffectRecord,
} from './reactive-v2'
import {
    EFFECT_DIRTY,
    setDirty,
} from './effect-flags'
import type { ScheduleEffectFn } from './scheduler'

export interface ComputedSignal<T> {
    readonly id: number
    /** Read the computed value. Lazily re-computes if dirty. */
    readonly get: () => T
    /** Force stop the computed (unsubscribes from all deps). */
    stop: () => void
    /** @internal — the underlying signal container for triggerV2 */
    readonly _container: SignalContainer<T>
    /** @internal — the underlying effect record */
    readonly _effect: EffectRecord
}

/**
 * Create a lazy computed signal.
 *
 * The computed re-evaluates `fn` only when one of its tracked deps changes AND
 * the value is actually read (lazy / pull-based).
 */
export function createComputedV2<T>(
    fn: () => T,
    scheduleEffect: ScheduleEffectFn
): ComputedSignal<T> {
    // Use a sentinel to detect first run
    const UNINITIALIZED = Symbol('uninit')
    let cachedValue: T | typeof UNINITIALIZED = UNINITIALIZED

    // The container exposes the computed value as a signal so downstream effects can track it
    const container = makeSignalContainer<T>(undefined as unknown as T)

    const effect = makeEffectRecord(() => {
        const newValue = fn()
        if (!Object.is(newValue, cachedValue)) {
            cachedValue = newValue
            container.value = newValue
            // Notify downstream effects that read this computed
            triggerV2(container, scheduleEffect)
        }
    })

    // Mark dirty so first read triggers computation
    effect.flags = setDirty(effect.flags)

    function get(): T {
        // If dirty, re-compute before returning
        if ((effect.flags & EFFECT_DIRTY) !== 0 || cachedValue === UNINITIALIZED) {
            runEffectV2(effect)
        }
        // Track this computed as a dependency of any outer effect
        trackV2(container)
        return container.value
    }

    return {
        get id() { return container.id },
        get,
        stop() { stopEffectV2(effect) },
        get _container() { return container },
        get _effect() { return effect },
    }
}
